import React, { useState, useEffect } from "react";
import defaultpet from "./assets/img/defaultpet.png";
import banner from "./assets/img/banner.png";
import { useParams, useNavigate } from "react-router-dom";

function VetAppointmentWizardPage() {
    const navigate = useNavigate();
    const { tagId: urlTagId } = useParams();

    // Wizard state
    const [step, setStep] = useState(1);
    const [tagId, setTagId] = useState("");
    const [petProfile, setPetProfile] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [appointmentType, setAppointmentType] = useState("once");
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [topic, setTopic] = useState("");
    const [description, setDescription] = useState("");
    const [repeatInterval, setRepeatInterval] = useState("weekly");

    // Simulate API base URL
    //const apiBaseUrl = "https://mock-api.wagtag.com";
    const apiBaseUrl = 'https://systalk-prod.azure-api.net/wagtag';

    // Fetch pet profile if tagId is present in URL
    useEffect(() => {
        if (urlTagId) {
            setTagId(urlTagId);
            setStep(1); // Always start at welcome
        }

        const fetchPetDetails = async () => {
            if (!urlTagId) return;

            setTagId(urlTagId);

            try {
                // Replace with real API call when available
                const response = await fetch(`${apiBaseUrl}/api/PetProfile/PetNameImage?tagProductCode=${urlTagId}`);
                if (!response.ok) throw new Error("Failed to fetch pet details.");
                const petData = await response.json();

                if (petData) {
                    setPetProfile({
                        name: petData.Name,
                        imageUri: petData.ImageUri || defaultpet,
                        description: petData.Description,
                        temperament: petData.Temperament,
                    });
                    setErrorMessage("");
                } else {
                    setPetProfile(null);
                    setErrorMessage("Pet not found. Please check the Tag ID.");
                }
            } catch (error) {
                setPetProfile(null);
                setErrorMessage("Pet not found. Please check the Tag ID.");
            }
        };

        fetchPetDetails();
    }, [urlTagId]);

    // Fetch pet profile when tagId is entered manually
    useEffect(() => {
        const cleanedTagId = tagId.replace(/-/g, '');

        if (
            cleanedTagId &&
            cleanedTagId.length === 8 &&
            cleanedTagId !== urlTagId
        ) {
            const fetchPetDetails = async () => {
                try {
                    // Replace with real API call when available
                    const response = await fetch(`${apiBaseUrl}/api/PetProfile/PetNameImage?tagProductCode=${cleanedTagId}`);
                    if (!response.ok) throw new Error("Failed to fetch pet details.");
                    const petData = await response.json();

                    if (petData) {
                        setPetProfile({
                            name: petData.Name,
                            imageUri: petData.ImageUri || defaultpet,
                            description: petData.Description,
                            temperament: petData.Temperament,
                        });
                        setErrorMessage("");
                    } else {
                        setPetProfile(null);
                        setErrorMessage("Pet not found. Please check the Tag ID.");
                    }
                } catch (error) {
                    setPetProfile(null);
                    setErrorMessage("Pet not found. Please check the Tag ID.");
                }
            };
            fetchPetDetails();
        }
    }, [tagId, urlTagId, apiBaseUrl]);

    // Wizard navigation
    const nextStep = () => setStep((s) => s + 1);
    const prevStep = () => setStep((s) => s - 1);

    // Wizard steps
    function renderWelcomeStep() {
        return (
            <div style={styles.formRow}>
                <h3>Vet Appointment Wizard</h3>
                <p style={styles.infoText}>
                    Schedule a new appointment for a pet. Press next to begin.
                </p>
            </div>
        );
    }

    function renderTagIdStep() {
        return (
            <div style={styles.formRow}>
                <h3>Enter the Pet's Tag ID</h3>
                <input
                    id="tagId"
                    type="text"
                    value={tagId}
                    onChange={(e) => setTagId(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
                    placeholder="Enter 8-digit Tag ID"
                    style={styles.textInput}
                />
                {errorMessage && <span style={styles.errorMessage}>{errorMessage}</span>}
            </div>
        );
    }

    function renderPetProfileStep() {
        if (!petProfile) return null;
        // Hardcoded example data for demonstration
        const lastAppointmentDate = "2025-09-15";
        const lastAppointmentReason = "Annual Vaccination";
        const age = "4 years";
        const sex = "Male";

        return (
            <div style={{marginBottom: "10px"}}>
                <div style={styles.petHeaderRow}>
                    <strong>Last Appointment:</strong> <span>{lastAppointmentDate}</span>
                </div>
                <div style={styles.petHeaderRow}>
                    <strong>Reason:</strong> <span>{lastAppointmentReason}</span>
                </div>
                <div style={styles.petHeaderRow}>
                    <strong>Temperament:</strong> <span>{petProfile.temperament}</span>
                </div>
                <div style={styles.petHeaderRow}>
                    <strong>Age:</strong> <span>{age}</span>
                </div>
                <div style={styles.petHeaderRow}>
                    <strong>Sex:</strong> <span>{sex}</span>
                </div>
            </div>
        );
    }

    function renderAppointmentTypeStep() {
        return (
            <div style={styles.formRow}>
                <h3>Appointment Type</h3>
                <div style={styles.yesNoButtonRow}>
                    <button
                        style={{
                            ...styles.yesButton,
                            backgroundColor: appointmentType === "once" ? "#43a047" : "#e0e0e0",
                            color: appointmentType === "once" ? "white" : "#333",
                        }}
                        onClick={() => setAppointmentType("once")}
                    >
                        Once Off
                    </button>
                    <button
                        style={{
                            ...styles.noButton,
                            backgroundColor: appointmentType === "repeat" ? "#e53935" : "#e0e0e0",
                            color: appointmentType === "repeat" ? "white" : "#333",
                        }}
                        onClick={() => setAppointmentType("repeat")}
                    >
                        Repeating
                    </button>
                </div>
                {appointmentType === "repeat" && (
                    <div style={{ marginTop: "16px" }}>
                        <label>
                            Repeat Interval:&nbsp;
                            <select
                                value={repeatInterval}
                                onChange={(e) => setRepeatInterval(e.target.value)}
                                style={styles.textInput}
                            >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </label>
                    </div>
                )}
            </div>
        );
    }

    function renderAppointmentDetailsStep() {
        return (
            <div style={styles.formRow}>
                <h3>Appointment Details</h3>
                <label>
                    Date:
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        style={styles.textInput}
                    />
                </label>
                <label>
                    Time:
                    <input
                        type="time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        style={styles.textInput}
                    />
                </label>
                <label>
                    Topic:
                    <input
                        type="text"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        style={styles.textInput}
                        placeholder="Appointment topic"
                    />
                </label>
                <label>
                    Description:
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        style={styles.textArea}
                        placeholder="Describe the reason for the appointment"
                        rows={4}
                    />
                </label>
            </div>
        );
    }

    function renderReviewStep() {
        return (
            <div style={styles.formRow}>
                <h3>Review Appointment</h3>
                <div>
                    <strong>Pet:</strong> {petProfile?.name || tagId}
                </div>
                <div>
                    <strong>Type:</strong> {appointmentType === "once" ? "Once Off" : `Repeating (${repeatInterval})`}
                </div>
                <div>
                    <strong>Date:</strong> {date}
                </div>
                <div>
                    <strong>Time:</strong> {time}
                </div>
                <div>
                    <strong>Topic:</strong> {topic}
                </div>
                <div>
                    <strong>Description:</strong> {description}
                </div>
            </div>
        );
    }

    // Mock submit
    const handleSubmit = async () => {
        setErrorMessage("");
        setSuccessMessage("");
        // Simulate API call
        await new Promise((r) => setTimeout(r, 1000));
        setSuccessMessage("Appointment created successfully!");
        setTimeout(() => {
            navigate("/thank-you");
        }, 1200);
    };

    // Wizard step flow
    const renderStep = () => {
        switch (step) {
            case 1: return renderWelcomeStep();
            case 2: return urlTagId || tagId.length === 8 ? renderPetProfileStep() : renderTagIdStep();
            case 3: return renderAppointmentTypeStep();
            case 4: return renderAppointmentDetailsStep();
            case 5: return renderReviewStep();
            default: return null;
        }
    };

    const isFirstStep = step === 1;
    const isLastStep = step === 5;

    function renderNavigationButtons() {
        if (isLastStep) {
            return (
                <div style={styles.navigationRow}>
                    <div style={{ flex: 1 }}>
                        <button style={styles.navigationButton} onClick={prevStep}>Back</button>
                    </div>
                    <div style={{ flex: 1, textAlign: "right" }}>
                        <button style={styles.submitButton} onClick={handleSubmit}>
                            Create Appointment
                        </button>
                    </div>
                </div>
            );
        }
        return (
            <div style={styles.navigationRow}>
                <div style={{ flex: 1 }}>
                    {!isFirstStep && (
                        <button style={styles.navigationButton} onClick={prevStep}>
                            Back
                        </button>
                    )}
                </div>
                <div style={{ flex: 1, textAlign: "right" }}>
                    <button
                        style={styles.navigationButton}
                        onClick={nextStep}
                        disabled={
                            (step === 2 && !petProfile) ||
                            (step === 4 && (!date || !time || !topic))
                        }
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <header style={styles.header}>
                <div style={styles.headerImageContainer}>
                    <img src={banner} alt="WagTag" style={styles.imagePreview} />
                </div>
            </header>
            <main style={styles.main}>
                {petProfile && (
                    <div style={styles.petHeaderCard}>
                        <div style={styles.petHeaderImageColumn}>
                            <div style={styles.petHeaderImageContainer}>
                                <img
                                    src={petProfile.imageUri || defaultpet}
                                    alt="Pet Preview"
                                    style={styles.petHeaderImage}
                                />
                            </div>
                        </div>
                        <div style={styles.petHeaderInfo}>
                            <strong>
                                {petProfile.name}
                                {petProfile.temperament && `. ${petProfile.name} is ${petProfile.temperament}.`}
                            </strong>
                            {petProfile.description && (
                                <span style={styles.petHeaderDescription}>{petProfile.description}</span>
                            )}
                        </div>
                    </div>
                )}
                <div style={styles.form}>
                    {renderStep()}
                    {renderNavigationButtons()}
                </div>
                {successMessage && <p style={styles.successMessage}>{successMessage}</p>}
                {errorMessage && <p style={styles.errorMessage}>{errorMessage}</p>}
            </main>
            <footer style={styles.footer}>
                <p>
                    Visit us at <a href="https://www.wagtag.com.au" target="_blank" rel="noopener noreferrer">www.wagtag.com.au</a>
                </p>
            </footer>
        </div>
    );
}

const styles = {
    header: {
        backgroundColor: "#4CAF50",
        color: "white",
        padding: "5px 5px",
    },
    headerImageContainer: {
        width: "100%",
        maxWidth: "300px",
        margin: "0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
    },
    imagePreview: {
        width: "100%",
        maxHeight: "80px",
        height: "auto",
        objectFit: "contain",
        borderRadius: "8px",
    },
    main: {
        flex: 1,
        padding: "10px",
        maxWidth: "600px",
        margin: "0 auto",
        width: "95%",
    },
    petHeaderCard: {
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        backgroundColor: "#fff",
        border: "1px solid #ddd",
        borderRadius: "5px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
        padding: "5px",
        gap: "10px",
        marginBottom: "10px",
    },
    petHeaderImageColumn: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "10px",
    },
    petHeaderImageContainer: {
        width: "100px",
        height: "100px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid #ccc",
        borderRadius: "12px",
        backgroundColor: "#f9f9f9",
        overflow: "hidden",
        flexShrink: 0,
    },
    petHeaderImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "5px",
    },
    petHeaderInfo: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    },
    petHeaderRow: {
        fontSize: "15px",
        color: "#444",
        marginBottom: "4px",
    },
    petHeaderDescriptionFull: {
        width: "100%",
        fontSize: "15px",
        color: "#444",
        margin: "12px 0 0 0",
        textAlign: "left",
        background: "#f9f9f9",
        padding: "12px",
        borderRadius: "8px",
        boxSizing: "border-box",
        gridColumn: "1 / -1",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "5px",
    },
    formRow: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        marginBottom: "10px",
        marginRight: "20px",
    },
    textInput: {
        width: "100%",
        padding: "10px",
        fontSize: "14px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "4px",
        marginBottom: "8px",
    },
    textArea: {
        width: "100%",
        padding: "10px",
        fontSize: "14px",
        border: "1px solid #ccc",
        borderRadius: "4px",
        marginTop: "4px",
        marginBottom: "8px",
    },
    yesNoButtonRow: {
        display: "flex",
        justifyContent: "center",
        gap: "24px",
        marginTop: "18px",
        marginBottom: "18px",
    },
    yesButton: {
        padding: "16px 36px",
        backgroundColor: "#43a047",
        color: "white",
        fontSize: "18px",
        fontWeight: "bold",
        border: "none",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(67,160,71,0.12)",
        cursor: "pointer",
        transition: "background 0.2s",
    },
    noButton: {
        padding: "16px 36px",
        backgroundColor: "#e53935",
        color: "white",
        fontSize: "18px",
        fontWeight: "bold",
        border: "none",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(229,57,53,0.12)",
        cursor: "pointer",
        transition: "background 0.2s",
    },
    navigationRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        gap: "10px",
        marginTop: "auto",
    },
    navigationButton: {
        padding: "16px 32px",
        minWidth: "120px",
        backgroundColor: "#4CAF50",
        color: "white",
        fontSize: "16px",
        fontWeight: "bold",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        alignSelf: "flex-start",
    },
    submitButton: {
        padding: "16px 32px",
        minWidth: "210px",
        backgroundColor: "#4CAF50",
        color: "white",
        fontSize: "16px",
        fontWeight: "bold",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        alignSelf: "flex-start",
    },
    successMessage: {
        color: "green",
        marginTop: "10px",
    },
    errorMessage: {
        color: "red",
        marginTop: "10px",
    },
    footer: {
        backgroundColor: "#f1f1f1",
        padding: "10px 20px",
        textAlign: "center",
        borderTop: "1px solid #ccc",
    },
};

export default VetAppointmentWizardPage;